/**
 * Client-seitige Aufbereitung von Bild-Uploads.
 *
 * Wichtig: Das ist KEIN Sicherheitsersatz -- der Server prüft jedes Bild
 * unabhängig davon noch einmal vollständig. Diese Schicht sorgt dafür, dass
 *
 *  1. das Bild über ein <canvas> neu kodiert wird. Dabei werden ausschließlich
 *     die dekodierten Pixel übernommen; alles andere -- angehängter Code,
 *     eingebettete Skripte, EXIF-Daten inkl. GPS-Position -- fällt weg.
 *  2. die Datei auf eine vernünftige Größe skaliert wird, damit der Upload klein
 *     bleibt und keine riesigen Requests entstehen.
 *  3. offensichtlich ungültige Dateien gar nicht erst gesendet werden.
 */

// Erlaubte Eingabeformate. SVG ist bewusst nicht dabei: SVG ist XML und kann
// Skripte enthalten.
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

// Obergrenze für die Datei, die der Nutzer auswählt (vor dem Verkleinern).
const MAX_INPUT_BYTES = 15 * 1024 * 1024;

// Zielgröße nach dem Verkleinern.
const MAX_EDGE = 1920;

export const ACCEPT_ATTRIBUTE = ACCEPTED_TYPES.join(',');

export class ImageUploadError extends Error {}

/** Liest die ersten Bytes einer Datei, um das echte Format zu bestimmen. */
const sniffType = async (file: File): Promise<string | null> => {
  const head = new Uint8Array(await file.slice(0, 16).arrayBuffer());

  const matches = (sig: number[], offset = 0) =>
    sig.every((b, i) => head[offset + i] === b);

  if (matches([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png';
  if (matches([0xff, 0xd8, 0xff])) return 'image/jpeg';
  if (matches([0x47, 0x49, 0x46, 0x38])) return 'image/gif';
  if (matches([0x52, 0x49, 0x46, 0x46]) && matches([0x57, 0x45, 0x42, 0x50], 8)) return 'image/webp';

  return null;
};

const loadImage = (blobUrl: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new ImageUploadError('Bild konnte nicht gelesen werden.'));
    img.src = blobUrl;
  });

/**
 * Wandelt eine ausgewählte Datei in eine bereinigte, verkleinerte Data-URL um.
 * Wirft ImageUploadError mit einer für den Nutzer lesbaren Meldung.
 */
export const prepareImageForUpload = async (file: File): Promise<string> => {
  if (file.size > MAX_INPUT_BYTES) {
    throw new ImageUploadError('Datei ist zu groß (max. 15 MB).');
  }

  // Nicht dem vom Browser gemeldeten Typ vertrauen, sondern die Bytes ansehen.
  const actualType = await sniffType(file);
  if (!actualType) {
    throw new ImageUploadError('Nur PNG, JPEG, GIF oder WebP sind erlaubt.');
  }

  const blobUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(blobUrl);

    const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    const width = Math.max(1, Math.round(img.naturalWidth * scale));
    const height = Math.max(1, Math.round(img.naturalHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new ImageUploadError('Bild konnte nicht verarbeitet werden.');
    }

    // Neu zeichnen = nur Pixel übernehmen, alle Metadaten und Anhänge verwerfen.
    ctx.drawImage(img, 0, 0, width, height);

    // PNG behält Transparenz, alles andere wird als JPEG deutlich kleiner.
    const outputType = actualType === 'image/png' ? 'image/png' : 'image/jpeg';
    const dataUrl = canvas.toDataURL(outputType, 0.85);

    if (!dataUrl.startsWith('data:image/')) {
      throw new ImageUploadError('Bild konnte nicht verarbeitet werden.');
    }

    return dataUrl;
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
};
